import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Users, Scale } from "lucide-react";
import type { Persona } from "@/components/landing/HeroSandbox";

interface RoleCardData {
  key: Persona;
  icon: typeof Camera;
  label: string;
  benefit: string;
}

const roles: RoleCardData[] = [
  { key: "blogger", icon: Camera, label: "Блогер", benefit: "Сканируй с Mobile Scout — штраф 0 ₽" },
  { key: "agency", icon: Users, label: "Агентство", benefit: "Массовая маркировка 500+ креативов/мес" },
  { key: "lawyer", icon: Scale, label: "Юрист", benefit: "AI-аудит по ФЗ-347 + ФЗ-168 за секунды" },
];

export function RoleCards({
  active,
  onChange,
}: {
  active: Persona;
  onChange: (p: Persona) => void;
}) {
  const [hovered, setHovered] = useState<Persona | null>(null);

  return (
    <div className="grid grid-cols-3 gap-3">
      {roles.map((r) => {
        const isActive = active === r.key;
        const isHovered = hovered === r.key;
        const Icon = r.icon;
        return (
          <motion.button
            key={r.key}
            onMouseEnter={() => setHovered(r.key)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onChange(r.key)}
            whileHover={{ y: -4, scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className={`relative flex flex-col items-center gap-2 rounded-2xl border p-4 transition-all duration-300 cursor-pointer min-h-[120px] ${
              isActive
                ? "border-primary/50 bg-primary/10 shadow-lg shadow-primary/10"
                : "border-border/40 bg-secondary/20 hover:border-primary/30 hover:bg-secondary/30"
            }`}
          >
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${
                isActive ? "bg-primary/20" : "bg-secondary/50"
              }`}
            >
              <Icon className={`h-5 w-5 ${isActive ? "text-primary" : "text-muted-foreground"}`} />
            </div>
            <span className={`text-xs font-semibold ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
              {r.label}
            </span>
            <AnimatePresence>
              {(isHovered || isActive) && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-[10px] leading-tight text-center overflow-hidden"
                  style={{ color: 'hsl(var(--primary-light))' }}
                >
                  {r.benefit}
                </motion.p>
              )}
            </AnimatePresence>
          </motion.button>
        );
      })}
    </div>
  );
}
